import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const fmt = v => 'R$ ' + parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
// Formata datas puras (colunas DATE) sem passar por new Date(), que interpretaria
// "YYYY-MM-DD" como meia-noite UTC e, em fuso UTC-3, exibiria o dia anterior.
const fmtData = d => {
  if (!d) return '—';
  const s = String(d).slice(0, 10); // pega só YYYY-MM-DD (ignora hora/Z se vier ISO)
  const [y, m, dd] = s.split('-');
  return (y && m && dd) ? `${dd}/${m}/${y}` : new Date(d).toLocaleDateString('pt-BR');
};
const toISO = d => d.toISOString().split('T')[0];
const inpStyle = { width: '100%', padding: '8px 10px', background: '#F4F6F9', border: '0.5px solid rgba(26,58,92,0.15)', borderRadius: '7px', color: '#1C2533', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif", boxSizing: 'border-box' };
const lbl = { color: '#6B7A8D', fontSize: '11px', fontWeight: '600', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.3px' };
const th = { padding: '10px 12px', background: '#F5F7FA', color: '#9AA3AE', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'left', borderBottom: '0.5px solid rgba(26,58,92,0.1)' };
const td = { padding: '12px 12px', borderBottom: '0.5px solid rgba(26,58,92,0.07)', fontSize: '13px', color: '#1C2533', verticalAlign: 'middle' };

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const ABAS = ['Vendas', 'Canais', 'Fluxo de Caixa', 'Contas a Pagar', 'Contas a Receber', 'Contas Pagas', 'Contas Recebidas'];

const filtros = [
  { key: 'mes', label: 'Este mês' },
  { key: 'trimestre', label: 'Últimos 3 meses' },
  { key: 'semestre', label: 'Últimos 6 meses' },
  { key: 'ano', label: 'Este ano' },
];

// Valor compacto para células da tabela de fluxo (R$ omitido, despesas entre parênteses)
const fmtCel = (v, despesa) => {
  const n = parseFloat(v || 0);
  if (n === 0) return '—';
  const s = Math.abs(n).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
  return despesa ? `(${s})` : s;
};

// ============================================================
// Relatório de contas (estilo Nibo)
// endpoint: rota do backend; tipo: 'despesa' | 'receita'
// nomeLabel: rótulo da coluna Nome (Fornecedor / Cliente)
// cor: cor de destaque do total/valores
// modo: 'aberto' (a pagar/receber, filtra por vencimento) |
//       'pago'   (pagas/recebidas, filtra por data de pagamento)
// ============================================================
function ContasReport({ endpoint, tipo, nomeLabel, cor, modo = 'aberto' }) {
  const pago = modo === 'pago';
  const hoje = new Date();
  const hojeISO = toISO(hoje);
  // Aberto: sem corte de data (todas as contas em aberto, vencidas e futuras).
  // Pago: padrão = mês corrente (01/mês até hoje).
  const [inicio, setInicio] = useState(pago ? toISO(new Date(hoje.getFullYear(), hoje.getMonth(), 1)) : '');
  const [fim, setFim] = useState(pago ? hojeISO : '');
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [valorMin, setValorMin] = useState('');
  const [valorMax, setValorMax] = useState('');
  const [categoriaId, setCategoriaId] = useState('');
  const [categorias, setCategorias] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Vencido só se aplica a contas em aberto (pagas nunca ficam em vermelho).
  const isVencido = d => !pago && d && String(d).slice(0, 10) < hojeISO;

  // Rótulos que mudam conforme o modo
  const dataLabel = pago ? 'Pagamento' : 'Vencimento';
  const valorColLabel = pago ? 'Valor pago' : 'Valor em aberto';
  const ultColLabel = pago ? 'Data pagamento' : 'Valor em aberto';

  const buscar = useCallback(async () => {
    setLoading(true);
    try {
      const params = { inicio, fim };
      if (nome) params.nome = nome;
      if (descricao) params.descricao = descricao;
      if (categoriaId) params.categoria_id = categoriaId;
      if (valorMin) params.valor_min = valorMin;
      if (valorMax) params.valor_max = valorMax;
      const r = await api.get(endpoint, { params });
      setData(r.data);
    } catch (e) { alert('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setLoading(false); }
  }, [endpoint, inicio, fim, nome, descricao, categoriaId, valorMin, valorMax]);

  // Carrega categorias do tipo correspondente + busca inicial (uma vez)
  useEffect(() => {
    api.get('/financeiro/categorias')
      .then(r => setCategorias((r.data || []).filter(c => c.tipo === tipo)))
      .catch(() => {});
    buscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  const exportarCSV = () => {
    if (!data || !data.lancamentos.length) return;
    const headers = ['Vencimento', nomeLabel, 'Descrição', 'Categoria', 'Recorrente',
      pago ? 'Data pagamento' : 'Valor', pago ? 'Valor pago' : 'Valor em aberto'];
    const linhas = data.lancamentos.map(l => [
      fmtData(l.data_vencimento),
      l.nome || '',
      l.descricao || '',
      l.categoria_nome || '',
      l.recorrente ? 'Sim' : 'Não',
      pago ? fmtData(l.data_pagamento) : parseFloat(l.valor || 0).toFixed(2).replace('.', ','),
      parseFloat(l.valor || 0).toFixed(2).replace('.', ','),
    ]);
    const csv = [headers, ...linhas]
      .map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(';'))
      .join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${endpoint.split('/').pop()}${inicio || fim ? `-${inicio || 'inicio'}-a-${fim || 'fim'}` : ''}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const fld = { flex: 1, minWidth: '150px' };

  // Layout estilo Nibo: filtros fixos no topo, tabela com scroll interno,
  // thead e tfoot (Total) sticky dentro do scroll.
  const wrapStyle = { display: 'flex', flexDirection: 'column', height: 'calc(100vh - 230px)', minHeight: '360px' };
  const stickyTh = { position: 'sticky', top: 0, zIndex: 3, background: '#F5F7FA', boxShadow: 'inset 0 -1px 0 rgba(26,58,92,0.12)' };
  const footSticky = { position: 'sticky', bottom: 0, background: '#F8FAFB', zIndex: 3, boxShadow: 'inset 0 1px 0 rgba(26,58,92,0.18)', borderBottom: 'none' };

  return (
    <div style={wrapStyle}>
      {/* ---- Filtros (fixos, fora do scroll) ---- */}
      <div style={{ flexShrink: 0, background: '#fff', border: '0.5px solid rgba(26,58,92,0.12)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '18px 20px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={fld}>
            <label style={lbl}>{dataLabel} — Início</label>
            <input type='date' value={inicio} onChange={e => setInicio(e.target.value)} style={inpStyle} />
          </div>
          <div style={fld}>
            <label style={lbl}>{dataLabel} — Fim</label>
            <input type='date' value={fim} onChange={e => setFim(e.target.value)} style={inpStyle} />
          </div>
          <div style={fld}>
            <label style={lbl}>{nomeLabel}</label>
            <input type='text' value={nome} onChange={e => setNome(e.target.value)} placeholder={`Buscar ${nomeLabel.toLowerCase()}`} style={inpStyle} />
          </div>
          <div style={fld}>
            <label style={lbl}>Descrição ou referência</label>
            <input type='text' value={descricao} onChange={e => setDescricao(e.target.value)} placeholder='Buscar descrição' style={inpStyle} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'flex-end', marginTop: '14px' }}>
          <div style={fld}>
            <label style={lbl}>{valorColLabel} — De</label>
            <input type='number' step='0.01' value={valorMin} onChange={e => setValorMin(e.target.value)} placeholder='0,00' style={inpStyle} />
          </div>
          <div style={fld}>
            <label style={lbl}>{valorColLabel} — Até</label>
            <input type='number' step='0.01' value={valorMax} onChange={e => setValorMax(e.target.value)} placeholder='0,00' style={inpStyle} />
          </div>
          <div style={fld}>
            <label style={lbl}>Categoria</label>
            <select value={categoriaId} onChange={e => setCategoriaId(e.target.value)} style={inpStyle}>
              <option value=''>Todas</option>
              {categorias.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
          <button onClick={buscar} disabled={loading}
            style={{ padding: '9px 24px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif", whiteSpace: 'nowrap' }}>
            {loading ? 'Filtrando...' : 'Filtrar'}
          </button>
        </div>
      </div>

      {/* ---- Cabeçalho da tabela + exportar (fixos, fora do scroll) ---- */}
      <div style={{ flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ fontSize: '13px', color: '#6B7A8D' }}>
          {data ? `Exibindo ${data.quantidade} lançamento(s)` : '—'}
        </div>
        <button onClick={exportarCSV} disabled={!data || !data.lancamentos.length}
          style={{ padding: '8px 16px', background: '#F4F6F9', border: '0.5px solid rgba(26,58,92,0.15)', color: '#1A3A5C', borderRadius: '8px', cursor: (!data || !data.lancamentos.length) ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
          ⬇ Exportar CSV
        </button>
      </div>

      {/* ---- Tabela (scroll interno; thead e tfoot sticky) ---- */}
      <div style={{ flex: 1, minHeight: 0, background: '#fff', border: '0.5px solid rgba(26,58,92,0.12)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '0 18px', overflowY: 'auto', overflowX: 'auto', position: 'relative' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '820px' }}>
          <thead>
            <tr>
              {['Vencimento', nomeLabel, 'Descrição', 'Categoria', 'Recorrente'].map(h => <th key={h} style={{ ...th, ...stickyTh }}>{h}</th>)}
              <th style={{ ...th, ...stickyTh, textAlign: pago ? 'left' : 'right' }}>{pago ? 'Data pagamento' : 'Valor'}</th>
              <th style={{ ...th, ...stickyTh, textAlign: 'right' }}>{pago ? 'Valor pago' : ultColLabel}</th>
            </tr>
          </thead>
          <tbody>
            {(!data || data.lancamentos.length === 0) && (
              <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#9AA3AE', padding: '32px 0' }}>
                {loading ? 'Carregando...' : (pago ? 'Nenhum lançamento pago no período' : 'Nenhum lançamento em aberto no período')}
              </td></tr>
            )}
            {data && data.lancamentos.map(l => {
              const venc = isVencido(l.data_vencimento);
              return (
                <tr key={l.id}
                  onMouseEnter={e => e.currentTarget.style.background = '#F8FAFB'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: '12px', whiteSpace: 'nowrap', color: venc ? '#C0392B' : '#6B7A8D', fontWeight: venc ? '700' : '400' }}>
                    {fmtData(l.data_vencimento)}
                  </td>
                  <td style={{ ...td, fontWeight: '600', color: '#1C2533' }}>{l.nome || '—'}</td>
                  <td style={{ ...td, color: '#6B7A8D' }}>{l.descricao || '—'}</td>
                  <td style={td}>
                    {l.categoria_nome
                      ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: l.categoria_cor || '#9AA3AE', flexShrink: 0 }} />
                          {l.categoria_nome}
                        </span>
                      : <span style={{ color: '#9AA3AE' }}>—</span>}
                  </td>
                  <td style={{ ...td, textAlign: 'center' }} title={l.recorrente ? 'Recorrente' : ''}>
                    {l.recorrente ? <span style={{ color: '#1E5FA5', fontSize: '15px' }}>↻</span> : <span style={{ color: '#C9CFD6' }}>—</span>}
                  </td>
                  {pago ? (
                    <>
                      <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: '12px', whiteSpace: 'nowrap', color: '#6B7A8D' }}>{fmtData(l.data_pagamento)}</td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: "'DM Mono',monospace", fontSize: '12px', fontWeight: '700', color: cor }}>{fmt(l.valor)}</td>
                    </>
                  ) : (
                    <>
                      <td style={{ ...td, textAlign: 'right', fontFamily: "'DM Mono',monospace", fontSize: '12px', color: '#1C2533' }}>{fmt(l.valor)}</td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: "'DM Mono',monospace", fontSize: '12px', fontWeight: '700', color: venc ? '#C0392B' : cor }}>{fmt(l.valor_aberto)}</td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
          {data && data.lancamentos.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={5} style={{ ...td, ...footSticky, fontWeight: '700', color: '#1C2533' }}>Total</td>
                <td style={{ ...td, ...footSticky }} />
                <td style={{ ...td, ...footSticky, textAlign: 'right', fontFamily: "'DM Mono',monospace", fontWeight: '800', fontSize: '13px', color: cor }}>{fmt(data.total)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

export default function Relatorios() {
  const [aba, setAba] = useState('Vendas');

  // ---- Relatório de vendas ----
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filtroAtivo, setFiltroAtivo] = useState('');
  const [vendaSel, setVendaSel] = useState(null); // venda selecionada -> modal de detalhes

  // ---- Fluxo de caixa ----
  const anoAtual = new Date().getFullYear();
  const [ano, setAno] = useState(anoAtual);
  const [fluxo, setFluxo] = useState(null);
  const [loadingFluxo, setLoadingFluxo] = useState(false);
  const [detalhe, setDetalhe] = useState(null); // drill-down de uma celula (categoria+mes)

  // ---- Canais (origem_lead) ----
  const [cIni, setCIni] = useState('');
  const [cFim, setCFim] = useState('');
  const [canais, setCanais] = useState(null);
  const [loadingCanais, setLoadingCanais] = useState(false);

  const buscarCanais = async () => {
    setLoadingCanais(true);
    try {
      const r = await api.get('/relatorios/vendas-por-canal', { params: { inicio: cIni, fim: cFim } });
      setCanais(r.data);
    } catch (e) { alert('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setLoadingCanais(false); }
  };

  const buscarRapido = async (tipo) => {
    const h = new Date();
    let ini, fi;
    if (tipo === 'mes') { ini = new Date(h.getFullYear(), h.getMonth(), 1); fi = new Date(h.getFullYear(), h.getMonth() + 1, 0); }
    else if (tipo === 'trimestre') { ini = new Date(h.getFullYear(), h.getMonth() - 2, 1); fi = h; }
    else if (tipo === 'semestre') { ini = new Date(h.getFullYear(), h.getMonth() - 5, 1); fi = h; }
    else if (tipo === 'ano') { ini = new Date(h.getFullYear(), 0, 1); fi = h; }
    setFiltroAtivo(tipo);
    setInicio(toISO(ini));
    setFim(toISO(fi));
    setLoading(true);
    try {
      const r = await api.get('/relatorios/vendas-periodo', { params: { inicio: toISO(ini), fim: toISO(fi) } });
      setDados(r.data);
    } catch (e) { alert('Erro: ' + e.message); }
    finally { setLoading(false); }
  };

  const buscar = async () => {
    setLoading(true);
    try {
      const r = await api.get('/relatorios/vendas-periodo', { params: { inicio, fim } });
      setDados(r.data);
    } catch (e) { alert('Erro: ' + e.message); }
    finally { setLoading(false); }
  };

  const exportarCSVVendas = () => {
    if (!dados || !dados.vendas.length) return;
    const headers = ['Data Venda', 'Entrega', 'Veículo', 'Cliente', 'Vendedor', 'Valor Venda', 'Lucro Bruto'];
    const linhas = dados.vendas.map(v => [
      fmtData(v.data_venda),
      v.data_entrega ? fmtData(v.data_entrega) : 'Pendente',
      `${v.marca || ''} ${v.modelo || ''} ${v.ano_mod || ''}`.trim(),
      v.cliente_nome || '',
      v.vendedor_nome || '',
      parseFloat(v.valor_final || 0).toFixed(2).replace('.', ','),
      parseFloat(v.lucro_bruto || 0).toFixed(2).replace('.', ','),
    ]);
    const csv = [headers, ...linhas]
      .map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(';'))
      .join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vendas-${inicio || 'inicio'}-a-${fim || 'fim'}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const carregarFluxo = useCallback(async (a) => {
    setLoadingFluxo(true);
    try {
      const r = await api.get('/financeiro/fluxo-caixa', { params: { ano: a } });
      setFluxo(r.data);
    } catch (e) { alert('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setLoadingFluxo(false); }
  }, []);

  useEffect(() => {
    if (aba === 'Fluxo de Caixa') carregarFluxo(ano);
  }, [aba, ano, carregarFluxo]);

  useEffect(() => {
    if (aba === 'Canais' && !canais) buscarCanais();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aba]);

  // Abre o painel com os lançamentos individuais que compõem uma célula
  const abrirDetalhe = async (l, mesIdx, despesa) => {
    const tipo = despesa ? 'despesa' : 'receita';
    setDetalhe({ nome: l.nome, mes: mesIdx, despesa, loading: true, lancamentos: [], total: 0 });
    try {
      const r = await api.get('/financeiro/fluxo-caixa/lancamentos', {
        params: { ano, mes: mesIdx + 1, tipo, ids: (l.categoria_ids || []).join(',') },
      });
      setDetalhe(d => d && { ...d, loading: false, lancamentos: r.data.lancamentos, total: r.data.total });
    } catch (e) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
      setDetalhe(null);
    }
  };

  // estilos das células da tabela de fluxo
  const cellBase = { padding: '8px 10px', fontSize: '12px', fontFamily: "'DM Mono',monospace", textAlign: 'right', whiteSpace: 'nowrap', borderBottom: '0.5px solid rgba(26,58,92,0.06)' };
  const labelCell = { padding: '8px 12px', fontSize: '12.5px', color: '#1C2533', textAlign: 'left', borderBottom: '0.5px solid rgba(26,58,92,0.06)', whiteSpace: 'nowrap', position: 'sticky', left: 0, background: '#fff', zIndex: 1 };
  const mesAtualIdx = new Date().getMonth();

  const linhaCategoria = (l, despesa) => (
    <tr key={`${l.tipo}-${l.categoria_id}`} style={{ transition: 'background 0.1s' }}>
      <td style={{ ...labelCell }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: l.cor, flexShrink: 0 }} />
          {l.nome}
        </span>
      </td>
      {l.meses.map((v, i) => (
        <td key={i} onClick={v === 0 ? undefined : () => abrirDetalhe(l, i, despesa)}
          title={v === 0 ? undefined : 'Ver lançamentos'}
          style={{ ...cellBase, cursor: v === 0 ? 'default' : 'pointer', color: v === 0 ? '#C9CFD6' : (despesa ? '#C0392B' : '#2D7D4F'), background: i === mesAtualIdx && ano === anoAtual ? 'rgba(26,58,92,0.03)' : 'transparent', textDecoration: v === 0 ? 'none' : 'underline', textDecorationColor: 'rgba(26,58,92,0.18)', textUnderlineOffset: '2px' }}>
          {fmtCel(v, despesa)}
        </td>
      ))}
      <td style={{ ...cellBase, fontWeight: '700', color: despesa ? '#C0392B' : '#2D7D4F', background: '#F8FAFB' }}>{fmtCel(l.total, despesa)}</td>
    </tr>
  );

  const linhaTotal = (label, arr, totalAno, opts = {}) => (
    <tr>
      <td style={{ ...labelCell, fontWeight: '700', background: opts.bg || '#F0F3F7', color: opts.color || '#1C2533' }}>{label}</td>
      {arr.map((v, i) => (
        <td key={i} style={{ ...cellBase, fontWeight: '700', background: opts.bg || '#F0F3F7', color: opts.cell ? opts.cell(v) : (opts.color || '#1C2533') }}>
          {opts.pct ? (v === 0 ? '—' : v.toFixed(1) + '%') : fmtCel(v, opts.despesa)}
        </td>
      ))}
      <td style={{ ...cellBase, fontWeight: '700', background: opts.bgTot || opts.bg || '#E6EAF0', color: opts.cell ? opts.cell(totalAno || 0) : (opts.color || '#1C2533') }}>
        {opts.pct ? (totalAno == null ? '—' : totalAno.toFixed(1) + '%') : (totalAno != null ? fmtCel(totalAno, opts.despesa) : '')}
      </td>
    </tr>
  );

  return (
    <div>
      <div style={{ fontSize: '18px', fontWeight: '700', color: '#1C2533', marginBottom: '16px' }}>Relatórios</div>

      <div style={{ display: 'flex', gap: '4px', marginBottom: '18px', borderBottom: '1px solid rgba(26,58,92,0.1)' }}>
        {ABAS.map(a => (
          <button key={a} onClick={() => setAba(a)}
            style={{ padding: '8px 16px', background: 'none', border: 'none', borderBottom: aba === a ? '2px solid #1A6DFF' : '2px solid transparent', color: aba === a ? '#1A6DFF' : '#6B7A8D', cursor: 'pointer', fontWeight: aba === a ? '700' : '400', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif", marginBottom: '-1px' }}>
            {a}
          </button>
        ))}
      </div>

      {/* ===================== VENDAS ===================== */}
      {aba === 'Vendas' && (
        <div>
          <div style={{ background: '#fff', border: '0.5px solid rgba(26,58,92,0.12)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '18px 20px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
              {filtros.map(f => (
                <button key={f.key} onClick={() => buscarRapido(f.key)}
                  style={{ padding: '7px 14px', background: filtroAtivo === f.key ? '#1A3A5C' : '#F4F6F9', border: 'none', color: filtroAtivo === f.key ? '#fff' : '#6B7A8D', borderRadius: '7px', cursor: 'pointer', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: filtroAtivo === f.key ? '600' : '400' }}>
                  {f.label}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Período — Início</label>
                <input type='date' value={inicio} onChange={e => { setInicio(e.target.value); setFiltroAtivo(''); }} style={inpStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Fim</label>
                <input type='date' value={fim} onChange={e => { setFim(e.target.value); setFiltroAtivo(''); }} style={inpStyle} />
              </div>
              <button onClick={buscar} disabled={loading}
                style={{ padding: '9px 20px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif", whiteSpace: 'nowrap' }}>
                {loading ? 'Buscando...' : 'Buscar'}
              </button>
              <button onClick={exportarCSVVendas} disabled={!dados || !dados.vendas.length}
                style={{ padding: '8px 16px', background: '#F4F6F9', border: '0.5px solid rgba(26,58,92,0.15)', color: '#1A3A5C', borderRadius: '8px', cursor: (!dados || !dados.vendas.length) ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif", whiteSpace: 'nowrap' }}>
                ⬇ Exportar CSV
              </button>
            </div>
          </div>

          {dados && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '14px', marginBottom: '16px' }}>
                {[
                  { label: 'Vendas', val: dados.quantidade, color: '#1E5FA5', mono: false },
                  { label: 'Faturamento', val: fmt(dados.total_vendas), color: '#2D7D4F', mono: true },
                  { label: 'Lucro bruto', val: fmt(dados.total_lucro), color: '#B06000', mono: true },
                  { label: 'Ticket médio', val: fmt(dados.quantidade > 0 ? dados.total_vendas / dados.quantidade : 0), color: '#1E5FA5', mono: true },
                ].map(item => (
                  <div key={item.label} style={{ background: '#fff', border: '0.5px solid rgba(26,58,92,0.12)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '16px 18px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#6B7A8D', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '10px' }}>{item.label}</div>
                    <div style={{ background: '#F4F6F9', borderRadius: '8px', padding: '12px 14px' }}>
                      <div style={{ fontSize: item.mono ? '18px' : '26px', fontWeight: '700', fontFamily: item.mono ? "'DM Mono',monospace" : "'DM Sans',system-ui,sans-serif", color: item.color }}>{item.val}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ background: '#fff', border: '0.5px solid rgba(26,58,92,0.12)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '16px 18px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>{['Data Venda', 'Entrega', 'Veículo', 'Cliente', 'Vendedor', 'Valor Venda', 'Lucro Bruto'].map(h => <th key={h} style={th}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {dados.vendas.length === 0 && <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#9AA3AE', padding: '32px 0' }}>Nenhuma venda no período</td></tr>}
                    {dados.vendas.map(v => (
                      <tr key={v.id} onClick={() => setVendaSel(v)} title='Ver detalhes da venda'
                        onMouseEnter={e => e.currentTarget.style.background = '#F8FAFB'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        style={{ cursor: 'pointer' }}>
                        <td style={{ ...td, color: '#6B7A8D', fontSize: '12px', fontFamily: "'DM Mono',monospace" }}>{fmtData(v.data_venda)}</td>
                        <td style={td}>
                          {v.data_entrega
                            ? <span style={{ background: '#EAF5EE', color: '#2D7D4F', padding: '2px 7px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' }}>{fmtData(v.data_entrega)}</span>
                            : <span style={{ background: '#FFF3E0', color: '#B06000', padding: '2px 7px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' }}>Pendente</span>}
                        </td>
                        <td style={{ ...td, fontWeight: '600', color: '#1E5FA5' }}>{v.marca} {v.modelo} {v.ano_mod}</td>
                        <td style={{ ...td, color: '#6B7A8D' }}>{v.cliente_nome}</td>
                        <td style={{ ...td, color: '#6B7A8D' }}>{v.vendedor_nome}</td>
                        <td style={{ ...td, color: '#2D7D4F', fontWeight: '700', fontFamily: "'DM Mono',monospace", fontSize: '12px' }}>{fmt(v.valor_final)}</td>
                        <td style={{ ...td, color: '#B06000', fontWeight: '700', fontFamily: "'DM Mono',monospace", fontSize: '12px' }}>{fmt(v.lucro_bruto)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Vendas por Origem — agrupa o período filtrado por canal de aquisição */}
              {(() => {
                const grupos = {};
                (dados.vendas || []).forEach(v => {
                  const k = (v.origem_lead && String(v.origem_lead).trim()) || 'Não informado';
                  if (!grupos[k]) grupos[k] = { origem: k, qtd: 0, valor: 0 };
                  grupos[k].qtd += 1;
                  grupos[k].valor += parseFloat(v.valor_final || 0);
                });
                const linhas = Object.values(grupos).sort((a, b) => b.qtd - a.qtd || b.valor - a.valor);
                const totalQtd = linhas.reduce((a, b) => a + b.qtd, 0);
                return (
                  <div style={{ background: '#fff', border: '0.5px solid rgba(26,58,92,0.12)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '16px 18px', marginTop: '16px' }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#1C2533', marginBottom: '14px' }}>Vendas por Origem</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>{['Origem', 'Qtd vendas', 'Valor total', '% do total'].map(h => <th key={h} style={th}>{h}</th>)}</tr>
                      </thead>
                      <tbody>
                        {linhas.length === 0 && <tr><td colSpan={4} style={{ ...td, textAlign: 'center', color: '#9AA3AE', padding: '32px 0' }}>Nenhuma venda no período</td></tr>}
                        {linhas.map(l => (
                          <tr key={l.origem}>
                            <td style={{ ...td, fontWeight: '600', color: '#1C2533' }}>{l.origem}</td>
                            <td style={{ ...td, color: '#6B7A8D' }}>{l.qtd}</td>
                            <td style={{ ...td, color: '#2D7D4F', fontWeight: '700', fontFamily: "'DM Mono',monospace", fontSize: '12px' }}>{fmt(l.valor)}</td>
                            <td style={{ ...td, color: '#6B7A8D', fontFamily: "'DM Mono',monospace", fontSize: '12px' }}>{totalQtd > 0 ? (l.qtd / totalQtd * 100).toFixed(1) : '0.0'}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* ===================== CANAIS ===================== */}
      {aba === 'Canais' && (
        <div>
          <div style={{ background: '#fff', border: '0.5px solid rgba(26,58,92,0.12)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '18px 20px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Período — Início</label>
                <input type='date' value={cIni} onChange={e => setCIni(e.target.value)} style={inpStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Fim</label>
                <input type='date' value={cFim} onChange={e => setCFim(e.target.value)} style={inpStyle} />
              </div>
              <button onClick={buscarCanais} disabled={loadingCanais}
                style={{ padding: '9px 20px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif", whiteSpace: 'nowrap' }}>
                {loadingCanais ? 'Buscando...' : 'Buscar'}
              </button>
            </div>
            <div style={{ fontSize: '11px', color: '#9AA3AE', marginTop: '10px' }}>Considera vendas efetivadas (exclui canceladas). Vendas sem canal aparecem como "Não informado".</div>
          </div>

          {canais && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '14px', marginBottom: '16px' }}>
                {[
                  { label: 'Vendas', val: canais.total_vendas, color: '#1E5FA5', mono: false },
                  { label: 'Faturamento', val: fmt(canais.total_faturamento), color: '#2D7D4F', mono: true },
                ].map(item => (
                  <div key={item.label} style={{ background: '#fff', border: '0.5px solid rgba(26,58,92,0.12)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '16px 18px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#6B7A8D', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '10px' }}>{item.label}</div>
                    <div style={{ background: '#F4F6F9', borderRadius: '8px', padding: '12px 14px' }}>
                      <div style={{ fontSize: item.mono ? '18px' : '26px', fontWeight: '700', fontFamily: item.mono ? "'DM Mono',monospace" : "'DM Sans',system-ui,sans-serif", color: item.color }}>{item.val}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ background: '#fff', border: '0.5px solid rgba(26,58,92,0.12)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '16px 18px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>{['Canal', 'Nº vendas', 'Faturamento', '% do faturamento'].map(h => <th key={h} style={th}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {canais.canais.length === 0 && <tr><td colSpan={4} style={{ ...td, textAlign: 'center', color: '#9AA3AE', padding: '32px 0' }}>Nenhuma venda no período</td></tr>}
                    {canais.canais.map(c => (
                      <tr key={c.canal}>
                        <td style={{ ...td, fontWeight: '600', color: c.canal === 'Não informado' ? '#9AA3AE' : '#1C2533' }}>{c.canal}</td>
                        <td style={{ ...td, color: '#6B7A8D' }}>{c.qtd}</td>
                        <td style={{ ...td, color: '#2D7D4F', fontWeight: '700', fontFamily: "'DM Mono',monospace", fontSize: '12px' }}>{fmt(c.faturamento)}</td>
                        <td style={{ ...td, color: '#6B7A8D', fontFamily: "'DM Mono',monospace", fontSize: '12px' }}>{canais.total_faturamento > 0 ? (c.faturamento / canais.total_faturamento * 100).toFixed(1) : '0.0'}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===================== FLUXO DE CAIXA ===================== */}
      {aba === 'Fluxo de Caixa' && (
        <div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '14px' }}>
            <button onClick={() => setAno(a => a - 1)} style={{ padding: '7px 12px', background: '#F4F6F9', border: '0.5px solid rgba(26,58,92,0.15)', borderRadius: '7px', cursor: 'pointer', fontSize: '14px', color: '#1A3A5C' }}>‹</button>
            <div style={{ fontSize: '15px', fontWeight: '700', color: '#1C2533', minWidth: '60px', textAlign: 'center' }}>{ano}</div>
            <button onClick={() => setAno(a => a + 1)} disabled={ano >= anoAtual} style={{ padding: '7px 12px', background: '#F4F6F9', border: '0.5px solid rgba(26,58,92,0.15)', borderRadius: '7px', cursor: ano >= anoAtual ? 'not-allowed' : 'pointer', fontSize: '14px', color: ano >= anoAtual ? '#C9CFD6' : '#1A3A5C' }}>›</button>
            <div style={{ fontSize: '12px', color: '#9AA3AE', marginLeft: '8px' }}>Valores realizados (lançamentos pagos). Despesas entre parênteses.</div>
          </div>

          {loadingFluxo && <div style={{ color: '#9AA3AE', fontSize: '13px', padding: '20px' }}>Carregando...</div>}

          {fluxo && !loadingFluxo && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '14px', marginBottom: '16px' }}>
                {[
                  { label: 'Receitas no ano', val: fluxo.totais.receitas_ano, color: '#2D7D4F' },
                  { label: 'Despesas no ano', val: fluxo.totais.despesas_ano, color: '#C0392B' },
                  { label: 'Resultado no ano', val: fluxo.totais.resultado_ano, color: fluxo.totais.resultado_ano >= 0 ? '#2D7D4F' : '#C0392B' },
                ].map(item => (
                  <div key={item.label} style={{ background: '#fff', border: '0.5px solid rgba(26,58,92,0.12)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '16px 18px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#6B7A8D', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '8px' }}>{item.label}</div>
                    <div style={{ fontSize: '20px', fontWeight: '700', fontFamily: "'DM Mono',monospace", color: item.color }}>{fmt(item.val)}</div>
                  </div>
                ))}
              </div>

              <div style={{ background: '#fff', border: '0.5px solid rgba(26,58,92,0.12)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '12px', textAlign: 'left', fontSize: '11px', color: '#9AA3AE', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px', borderBottom: '1px solid rgba(26,58,92,0.12)', position: 'sticky', left: 0, background: '#F5F7FA', zIndex: 2 }}>Categoria</th>
                      {MESES.map((m, i) => (
                        <th key={m} style={{ padding: '12px 10px', textAlign: 'right', fontSize: '11px', letterSpacing: '0.4px', color: i === mesAtualIdx && ano === anoAtual ? '#1A3A5C' : '#9AA3AE', fontWeight: '700', textTransform: 'uppercase', borderBottom: '1px solid rgba(26,58,92,0.12)', background: i === mesAtualIdx && ano === anoAtual ? 'rgba(26,58,92,0.04)' : 'transparent' }}>{m}</th>
                      ))}
                      <th style={{ padding: '12px 10px', textAlign: 'right', fontSize: '11px', letterSpacing: '0.4px', color: '#1C2533', fontWeight: '700', textTransform: 'uppercase', borderBottom: '1px solid rgba(26,58,92,0.12)', background: '#F8FAFB' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* RECEITAS */}
                    <tr><td colSpan={14} style={{ padding: '8px 12px', fontSize: '11px', fontWeight: '700', color: '#2D7D4F', textTransform: 'uppercase', letterSpacing: '0.4px', background: '#EAF5EE' }}>Receitas</td></tr>
                    {fluxo.receitas.length === 0 && <tr><td colSpan={14} style={{ padding: '12px', fontSize: '12px', color: '#9AA3AE', textAlign: 'center' }}>Sem receitas</td></tr>}
                    {fluxo.receitas.map(l => linhaCategoria(l, false))}
                    {linhaTotal('Total de receitas', fluxo.totais.receitas, fluxo.totais.receitas_ano, { bg: '#EAF5EE', color: '#2D7D4F', bgTot: '#D9EEE0' })}

                    {/* DESPESAS */}
                    <tr><td colSpan={14} style={{ padding: '8px 12px', fontSize: '11px', fontWeight: '700', color: '#C0392B', textTransform: 'uppercase', letterSpacing: '0.4px', background: '#FDECEA' }}>Despesas</td></tr>
                    {fluxo.despesas.length === 0 && <tr><td colSpan={14} style={{ padding: '12px', fontSize: '12px', color: '#9AA3AE', textAlign: 'center' }}>Sem despesas</td></tr>}
                    {fluxo.despesas.map(l => linhaCategoria(l, true))}
                    {linhaTotal('Total de despesas', fluxo.totais.despesas, fluxo.totais.despesas_ano, { bg: '#FDECEA', color: '#C0392B', bgTot: '#F8DAD5', despesa: true })}

                    {/* RESULTADO */}
                    {linhaTotal('= RESULTADO', fluxo.totais.resultado, fluxo.totais.resultado_ano, {
                      bg: '#1A3A5C', bgTot: '#12283F', color: '#fff',
                      cell: v => v >= 0 ? '#7DF5B8' : '#FF8080',
                    })}
                    {/* MARGEM */}
                    {linhaTotal('% Margem', fluxo.totais.margem, null, {
                      bg: '#F0F3F7', pct: true,
                      cell: v => v >= 0 ? '#2D7D4F' : '#C0392B',
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ===================== CONTAS A PAGAR ===================== */}
      {aba === 'Contas a Pagar' && (
        <ContasReport endpoint='/financeiro/relatorio/contas-a-pagar' tipo='despesa' nomeLabel='Fornecedor' cor='#C0392B' />
      )}

      {/* ===================== CONTAS A RECEBER ===================== */}
      {aba === 'Contas a Receber' && (
        <ContasReport endpoint='/financeiro/relatorio/contas-a-receber' tipo='receita' nomeLabel='Cliente' cor='#2D7D4F' />
      )}

      {/* ===================== CONTAS PAGAS ===================== */}
      {aba === 'Contas Pagas' && (
        <ContasReport endpoint='/financeiro/relatorio/contas-pagas' tipo='despesa' nomeLabel='Fornecedor' cor='#C0392B' modo='pago' />
      )}

      {/* ===================== CONTAS RECEBIDAS ===================== */}
      {aba === 'Contas Recebidas' && (
        <ContasReport endpoint='/financeiro/relatorio/contas-recebidas' tipo='receita' nomeLabel='Cliente' cor='#2D7D4F' modo='pago' />
      )}

      {/* ===================== DETALHE DA CÉLULA (lançamentos) ===================== */}
      {detalhe && (
        <div onClick={() => setDetalhe(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,25,40,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '12px', width: 'min(580px,94vw)', maxHeight: '82vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(12,20,30,0.28)' }}>
            <div style={{ padding: '16px 20px', borderBottom: '0.5px solid rgba(26,58,92,0.12)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: '15px', fontWeight: '700', color: '#1C2533' }}>{detalhe.nome}</div>
                <div style={{ fontSize: '12px', color: '#6B7A8D', marginTop: '3px' }}>
                  {detalhe.despesa ? 'Despesa' : 'Receita'} • {MESES[detalhe.mes]}/{ano}
                </div>
              </div>
              <button onClick={() => setDetalhe(null)}
                style={{ background: 'none', border: 'none', fontSize: '22px', color: '#9AA3AE', cursor: 'pointer', lineHeight: 1, padding: 0 }}>×</button>
            </div>
            <div style={{ overflowY: 'auto', padding: '4px 20px 8px' }}>
              {detalhe.loading && <div style={{ color: '#9AA3AE', fontSize: '13px', padding: '28px 0', textAlign: 'center' }}>Carregando...</div>}
              {!detalhe.loading && detalhe.lancamentos.length === 0 && <div style={{ color: '#9AA3AE', fontSize: '13px', padding: '28px 0', textAlign: 'center' }}>Nenhum lançamento.</div>}
              {!detalhe.loading && detalhe.lancamentos.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={th}>Data</th>
                      <th style={th}>Descrição</th>
                      <th style={th}>Status</th>
                      <th style={{ ...th, textAlign: 'right' }}>Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalhe.lancamentos.map(l => (
                      <tr key={l.id}>
                        <td style={{ ...td, color: '#6B7A8D', fontSize: '12px', fontFamily: "'DM Mono',monospace", whiteSpace: 'nowrap' }}>{fmtData(l.data_pagamento)}</td>
                        <td style={td}>{l.descricao || '—'}</td>
                        <td style={td}>
                          <span style={{ background: '#EAF5EE', color: '#2D7D4F', padding: '2px 7px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', textTransform: 'capitalize' }}>{l.status}</span>
                        </td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: '700', fontFamily: "'DM Mono',monospace", fontSize: '12px', color: detalhe.despesa ? '#C0392B' : '#2D7D4F', whiteSpace: 'nowrap' }}>{fmt(l.valor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {!detalhe.loading && detalhe.lancamentos.length > 0 && (
              <div style={{ padding: '12px 20px', borderTop: '0.5px solid rgba(26,58,92,0.12)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                <span style={{ color: '#6B7A8D', fontWeight: '600' }}>{detalhe.lancamentos.length} lançamento(s)</span>
                <span style={{ fontWeight: '700', fontFamily: "'DM Mono',monospace", color: detalhe.despesa ? '#C0392B' : '#2D7D4F' }}>{fmt(detalhe.total)}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===================== DETALHE DA VENDA ===================== */}
      {vendaSel && (() => {
        const vv = vendaSel;
        const compra = parseFloat(vv.valor_compra || 0);
        const finalV = parseFloat(vv.valor_final || 0);
        const lucro = parseFloat(vv.lucro_bruto || 0);
        const despesasV = finalV - compra - lucro; // derivado: lucro = final - compra - despesas
        const recebido = parseFloat(vv.valor_recebido || 0);
        const saldo = finalV - recebido;
        const linha = (label, valor, cor) => (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '8px 0', borderBottom: '0.5px solid rgba(26,58,92,0.07)' }}>
            <span style={{ fontSize: '13px', color: '#6B7A8D' }}>{label}</span>
            <span style={{ fontSize: '13px', fontWeight: '600', color: cor || '#1C2533', textAlign: 'right' }}>{valor}</span>
          </div>
        );
        const titulo = (t) => (
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#9AA3AE', textTransform: 'uppercase', letterSpacing: '0.4px', margin: '16px 0 4px' }}>{t}</div>
        );
        return (
          <div onClick={() => setVendaSel(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(15,25,40,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background: '#fff', borderRadius: '12px', width: 'min(560px,94vw)', maxHeight: '86vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(12,20,30,0.28)' }}>
              <div style={{ padding: '16px 20px', borderBottom: '0.5px solid rgba(26,58,92,0.12)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: '#1C2533' }}>{vv.marca} {vv.modelo} {vv.ano_mod}</div>
                  <div style={{ fontSize: '12px', color: '#6B7A8D', marginTop: '3px' }}>
                    Venda #{vv.id} • {fmtData(vv.data_venda)}{vv.placa ? ' • ' + vv.placa : ''}
                  </div>
                </div>
                <button onClick={() => setVendaSel(null)}
                  style={{ background: 'none', border: 'none', fontSize: '22px', color: '#9AA3AE', cursor: 'pointer', lineHeight: 1, padding: 0 }}>×</button>
              </div>
              <div style={{ overflowY: 'auto', padding: '8px 20px 16px' }}>
                {titulo('Dados da venda')}
                {linha('Cliente', vv.cliente_nome || '—')}
                {linha('Vendedor', vv.vendedor_nome || '—')}
                {linha('Forma de pagamento', vv.forma_pagamento || '—')}
                {linha('Entrega', vv.data_entrega ? fmtData(vv.data_entrega) : 'Pendente', vv.data_entrega ? '#2D7D4F' : '#B06000')}
                {vv.observacoes ? linha('Observações', vv.observacoes) : null}

                {titulo('Valores')}
                {parseFloat(vv.valor_veiculo || 0) > 0 ? linha('Valor do veículo', fmt(vv.valor_veiculo)) : null}
                {parseFloat(vv.valor_desconto || 0) > 0 ? linha('Desconto', '− ' + fmt(vv.valor_desconto), '#C0392B') : null}
                {linha('Valor final', fmt(finalV), '#2D7D4F')}
                {recebido > 0 ? linha('Recebido', fmt(recebido)) : null}
                {recebido > 0 && saldo > 0.009 ? linha('Saldo a receber', fmt(saldo), '#E67E22') : null}

                {titulo('Resultado')}
                {linha('Valor de compra', fmt(compra), '#1E5FA5')}
                {linha('Despesas do veículo', fmt(despesasV), '#C0392B')}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', padding: '12px 14px', background: lucro >= 0 ? '#EAF5EE' : '#FDECEA', borderRadius: '8px' }}>
                  <span style={{ fontSize: '13px', fontWeight: '700', color: '#1C2533' }}>Lucro bruto</span>
                  <span style={{ fontSize: '18px', fontWeight: '800', fontFamily: "'DM Mono',monospace", color: lucro >= 0 ? '#2D7D4F' : '#C0392B' }}>{fmt(lucro)}</span>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
